import React, { Component } from 'react';
import VisibilityIcon from '../../icons/VisibilityIcon';
import IndividualIcon from '../../icons/IndividualIcon';
import GroupOnlyPermanentIcon from '../../icons/GroupOnlyPermanentIcon';
import GroupOnlyTemporaryIcon from '../../icons/GroupOnlyTemporaryIcon';
import ReactTooltip from 'react-tooltip';
import VisibilityModal from 'components/modal/visibility/VisibilityModal';
import MixpanelLogger from '_legacy/diagnostics/logging/mixpanelLogger';
import { isGroupOnly, isRestricted } from '_legacy/utils/Visibility';
import { GROUP_ONLY_TEMPORARY_TYPE_VALUE } from '_legacy/constants/groupOnlyType';

class VisibilityRenderer extends Component {
    constructor(props) {
        super(props);
        this.elRef = React.createRef();
        this.state = { visibilityDialogOpened: false };
    }

    tooltipDelay = 400;

    clearTooltipTimer = () => {
        if (!this.tooltipTimer) {
            return;
        }
        window.clearTimeout(this.tooltipTimer);
        this.tooltipTimer = undefined;
    };

    openVisibilityDialog = () => {
        this.setState({ visibilityDialogOpened: true });
        this.sendOpenMixpanelEvent();
    };

    closeVisibilityDialog = (isSaved, isCanceled) => {
        this.setState({ visibilityDialogOpened: false });
        this.sendCloseMixpanelEvent(isSaved, isCanceled);
    };

    onVisibilitySettingsSaved = (result, sharers, previousSharers) => {
        this.props.agGridReact.props.onRowUpdated(result);
        this.sendSaveMixpanelEvent(result, sharers, previousSharers);
    };

    isIndividual(node) {
        return (
            node.data.visibility?.userIds?.length === 1 &&
            node.data.visibility?.groupIds?.length === 0 &&
            node.data.visibility?.userIds[0] === node.data.createdBy.userId
        );
    }

    isRestricted(node) {
        return isRestricted(node?.data?.visibility);
    }

    isGroupOnly(node) {
        return isGroupOnly(node?.data?.visibility);
    }

    isTemporarilyGroupOnly(node) {
        return (
            node.data.groupOnlyType === GROUP_ONLY_TEMPORARY_TYPE_VALUE ||
            Boolean(node.data.groupOnlyExpiryDate)
        );
    }

    sendOpenMixpanelEvent() {
        const data = this.props.node.data;
        const mixpanelProps = {
            itemId: data.id,
            itemType: this.getItemType(data.__typename, data.visibility),
            fortGroup: data.groupName,
            datasetId: data.datasetId,
        };
        MixpanelLogger.trackOpenSharingDialogEvent(mixpanelProps);
    }

    sendCloseMixpanelEvent(isSaved, isCanceled) {
        if (isSaved) {
            return;
        }
        const data = this.props.node.data;
        const mixpanelProps = {
            itemId: data.id,
            itemType: this.getItemType(data.__typename, data.visibility),
            fortGroup: data.groupName,
            datasetId: data.datasetId,
            method: isCanceled ? 'Cancel' : 'Click-away',
        };
        MixpanelLogger.trackCloseSharingDialogEvent(mixpanelProps);
    }

    sendSaveMixpanelEvent(result, sharers, previousSharers) {
        const data = this.props.node.data;

        const mixpanelProps = {
            itemId: data.id,
            itemType: this.getItemType(result.__typename, result.visibility),
            fortGroup: data.groupName,
            datasetId: data.datasetId,
            visibility: sharers.map((x) => {
                return { id: x.id, name: x.name };
            }),
            previousVisibility: previousSharers.map((x) => {
                return { id: x.id, name: x.name };
            }),
        };
        MixpanelLogger.trackChangeVisibilityEventNsc(mixpanelProps);
    }

    getItemType = (itemName, visibility) => {
        if (
            visibility.userIds.length === 1 &&
            visibility.groupIds.length === 0
        ) {
            return `Individual ${itemName}`;
        }
        if (visibility.userIds.length > 1 || visibility.groupIds.length > 1) {
            return `Restricted ${itemName}`;
        }
        if (
            visibility.userIds.length === 0 &&
            visibility.groupIds.length === 0
        ) {
            return `Public ${itemName}`;
        }
    };

    render() {
        const { node } = this.props;

        let displayValue;

        if (node && node.group) {
            displayValue = null;
        } else if (node && node.data && this.isIndividual(node)) {
            displayValue = (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <IndividualIcon />
                </div>
            );
        } else if (node && node.data && this.isGroupOnly(node)) {
            displayValue = (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {this.isTemporarilyGroupOnly(node) ? (
                        <GroupOnlyTemporaryIcon />
                    ) : (
                        <GroupOnlyPermanentIcon />
                    )}
                </div>
            );
        } else if (node && node.data && this.isRestricted(node)) {
            displayValue = (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <VisibilityIcon />
                </div>
            );
        }

        const rowIndex = `${this.props.rowIndex}`;

        return (
            <div
                ref={this.elRef}
                style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    minHeight: `${this.props.node.rowHeight - 1}px`,
                    cursor: 'pointer',
                }}
                data-tip={rowIndex}
                data-for={`${this.props.context.gridId}-group-only-expiry`}
                onMouseEnter={() => {
                    if (this.tooltipTimer) {
                        return; // prevent double triggering problem on some browsers
                    }

                    this.tooltipTimer = setTimeout(() => {
                        ReactTooltip.show(this.elRef.current);
                        this.clearTooltipTimer();
                    }, this.tooltipDelay);
                }}
                onMouseLeave={() => {
                    this.clearTooltipTimer();
                    ReactTooltip.hide(this.elRef.current);
                }}
                onDoubleClick={() => {
                    this.openVisibilityDialog();
                }}
            >
                {displayValue}
                {this.state.visibilityDialogOpened && (
                    <VisibilityModal
                        entityDetails={this.props.node.data}
                        onClose={this.closeVisibilityDialog}
                        onSave={this.onVisibilitySettingsSaved}
                        open={this.state.visibilityDialogOpened}
                        gridId={this.props.context.gridId}
                    />
                )}
            </div>
        );
    }
}

export default VisibilityRenderer;
